import { createSelector } from 'reselect'
import { isRetina } from 'src/lib/util/dom'
import attachment_types from 'src/lib/attachment-types' // eslint-disable-line camelcase
import request from 'src/lib/data/request'
import { getOrientationFromExifData } from 'src/lib/util/exifData'
import { attachmentProcessingStatus, mimeToCategory } from 'shared/constants'
import bytes from 'humanize-bytes'
import config from 'config'

export function getReadableFileSizeString(fileSizeInBytes) {
  let i = -1
  const byteUnits = [' kB', ' MB', ' GB', ' TB', ' PB', ' EB', ' ZB', ' YB']
  do {
    fileSizeInBytes /= 1024
    i += 1
  } while (fileSizeInBytes > 1024)

  return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i]
}

export const fileNameNoExtension = fileName =>
  (fileName && fileName.substring(0, fileName.lastIndexOf('.'))) || fileName

// TODO source env for client side
// this should be YD_S3_URL
export const urlSelector = createSelector(
  props => props[':attachment/uuid'],
  props =>
    props[':attachment/processing-status'] &&
    props[':attachment/processing-status'] ==
      attachmentProcessingStatus.TRANSCODING_COMPLETE,
  (attachmentUuid, completed) =>
    !completed
      ? `${config.NEXT_PUBLIC_YD_ATTACHMENT_URL}${encodeURIComponent(
          attachmentUuid
        )}`
      : `${config.NEXT_PUBLIC_YD_ATTACHMENT_URL}converted_${encodeURIComponent(
          fileNameNoExtension(attachmentUuid)
        )}.mp4`
)

/*
 * Adds a file extension if there is none
 *
 * @param extension: String (without the ".")
 * @param filePath: String
 */
export const defaultExtension = createSelector(
  extension => extension || '',
  (_, filePath) => filePath || '',
  (extension, filePath) => {
    const basePath = filePath.split('?')[0]
    const queryString = filePath.substr(basePath.length, filePath.length)

    return /\.\S+$/.test(
      basePath.substr(basePath.lastIndexOf('/') + 1, basePath.length)
    )
      ? filePath
      : `${basePath}.${extension}${queryString}`
  }
)

export function fileSuffix(suffix) {
  return isRetina && suffix ? `_${suffix}_retina` : suffix
}

// ==== attachment uploading

export const getOrientation = file => {
  return getOrientationFromExifData(file)
}

export const applyOrientation = (orientation, ctx, width, height) => {
  return orientationTransforms[orientation](ctx, width, height)
}

const orientationTransforms = {
  1: (_, __, ___) => () => null,
  2: (ctx, width, _) => ctx.transform(-1, 0, 0, 1, width, 0),
  3: (ctx, width, height) => ctx.transform(-1, 0, 0, -1, width, height),
  4: (ctx, _, height) => ctx.transform(1, 0, 0, -1, 0, height),
  5: (ctx, _, __) => ctx.transform(0, 1, 1, 0, 0, 0),
  6: (ctx, _, height) => ctx.transform(0, 1, -1, 0, height, 0),
  7: (ctx, width, height) => ctx.transform(0, -1, -1, 0, height, width),
  8: (ctx, width, _) => ctx.transform(0, -1, 1, 0, 0, width)
}

// Checking for max file size
export const maxSize = req => {
  const size = req.size
  const max = bytes(config.NEXT_PUBLIC_MAX_UPLOAD_FILE_SIZE)
  return size > max
}

const uploadFileToS3 = async ({
  file,
  tempId = 'temp_attachment_' + Date.now(),
  onProgress = () => null
}) => {
  const fileData = new FormData()
  fileData.append(file.name, file)
  const { body } = await request()
    .post('/api/post/attachments')
    .set('Yellowdig-Attachment-Temp-Id', tempId)
    .send(fileData)
    .on('progress', onProgress)

  return body
}

export const uploadAttachmentToS3 = async (file, onProgress) => {
  const body = await uploadFileToS3({ file, onProgress })
  return { uuid: body[':attachment/uuid'], url: urlSelector(body) }
}

const getAttachmentType = mime => {
  const category = mimeToCategory[mime]
  const type = category && attachment_types[category.toUpperCase()] // eslint-disable-line camelcase
  return type || ':file'
}

export const getGraphqlAttachmentType = mime => {
  const category = mimeToCategory[mime]
  const type = category && category.toUpperCase()
  return type || 'FILE'
}

export const transformToDatomic = ({
  uuid,
  name,
  caption,
  mime,
  size,
  status,
  v2,
  url
}) => ({
  ':attachment/type': getAttachmentType(mime),
  ':attachment/uuid': uuid,
  ':attachment/title': name,
  ':attachment/description': caption,
  ':attachment/mimetype': mime,
  ':attachment/size': size,
  ':attachment/processing-status': status,
  ...(v2 ? { ':attachment/v2-url': url } : {})
})
